import {
  LockOutlined,
  EditOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
  Table as ATable,
  Tag,
  Tooltip,
  Typography,
  Form,
  Select,
  Radio,
  message,
  Input,
  Checkbox,
  Modal,
  Button,
} from 'antd';
import colors from '../../../constants/ColourConstants';
import './Payouts.css';
import { useEffect, useState, useContext } from 'react';
import CustomButton from '../../common/Button/Button';
import {
  defaultRail,
  getPayoutsAndInvoices,
} from '../../../services/PayoutAPIs';
import { ExpandedRow } from './ExpandedRow';
import AuditLogs from '../AuditLogs/AuditLogsPopup';
import FileSize from '../../../constants/FileSize';
import {
  AppFactsContext,
  CountryContext,
  PermissionsContext,
} from '../../common/Navigation/Navigation';
import { PAYOUT_OVERRIDE_GENERATE } from '../../../constants/PermissionConstants';
import CompleteFailedPayoutModal from './Modals/CompleteFailedPayoutModal';

export default function PayoutsTable({
  data,
  loading,
  title,
  handleRowSelection,
  handleSort,
  sortField,
  handlePayoutsStatusUpdate,
  selectedRowKeys,
  payoutExpandedRowKeys,
  setPayoutExpandedRowKeys,
  handlePayouts,
  handleCompleteFailedPayout,
}) {
  const { Text } = Typography;
  const [editableRows, setEditableRows] = useState([]);
  const [expandedRecords, setExpandedRecords] = useState({});
  const [loadingTransactionData, setLoadingTransactionData] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [TransactionsLoader, SetTransactionsLoader] = useState('false');
  const [oldPayoutExpandedRowKeys, setOldPayoutExpandedRowKeys] = useState([]);
  const [defaultRailModel, setDefaultRailModel] = useState({
    key: '',
    open: false,
  });
  const [defaultRailLoader, setDeafultRailLoader] = useState(false);

  const [auditData, setAuditData] = useState({});
  const [completeModal, setCompleteModal] = useState({
    open: false,
    payoutId: null,
  });
  const [completeLoading, setCompleteLoading] = useState(false);

  const { countries } = useContext(CountryContext);
  const { permissions } = useContext(PermissionsContext);
  const { appFacts } = useContext(AppFactsContext);

  const manual_completion_source_status = ['Payout Failure', 'Payout Pending'];

  const canCompleteFailedPayout = (record) =>
    permissions?.includes(PAYOUT_OVERRIDE_GENERATE) &&
    manual_completion_source_status.includes(record.status) &&
    !record.reattempted;

  const isCompleteFailedPayoutLocked = Boolean(
    appFacts?.update_payouts ||
      appFacts?.prefail_payouts ||
      appFacts?.regenerate_payouts,
  );

  const handleCompleteSubmit = (payoutId, payload) => {
    setCompleteLoading(true);
    handleCompleteFailedPayout(payoutId, payload)
      .then((success) => {
        if (success) {
          setCompleteModal({ open: false, payoutId: null });
        }
      })
      .finally(() => {
        setCompleteLoading(false);
      });
  };

  const getFlag = (country) => {
    const countryObj = countries.find((c) => c.key === country);
    return countryObj
      ? `https://flagcdn.com/w40/${countryObj?.iso?.toLowerCase()}.png`
      : '';
  };

  useEffect(() => {
    setEditableRows([]);
  }, [data]);

  const dropdown_status = [
    'Ready for Finance Review',
    'Under Further Review',
    'Ready to Remit',
    'Voided',
  ];

  const handleRowLevelStatus = (value) => {
    const index = dropdown_status.indexOf(value);
    let dropdownOptions =
      index !== -1 ? dropdown_status.slice(index + 1) : dropdown_status;
    if (value === 'Ready to Remit') {
      dropdownOptions.unshift('Under Further Review');
      dropdownOptions.unshift('Payout Initiated');
    }
    return dropdownOptions.map((ele) => (
      <Radio value={ele} key={ele}>
        {ele}
      </Radio>
    ));
  };

  const handleEditColumn = (value, record, Type, key, dataIndex) => {
    const { payoutId, statusType, isLocked, striked, status, reattempted } =
      record;
    return editableRows.includes(payoutId) ? (
      <>
        <Form
          style={{ display: 'flex' }}
          onFinish={() => {
            handlePayoutsStatusUpdate(
              'single',
              payoutId,
              editedValues[payoutId],
            );
          }}
        >
          <Form.Item
            name={key}
            style={{ width: '65%', margin: 0, marginRight: 12 }}
          >
            {Type === 'Select' ? (
              <Select
                placeholder="Select status"
                defaultValue={record[dataIndex]}
                onChange={(val) => {
                  setEditedValues((prev) => ({
                    [payoutId]: {
                      ...(prev[payoutId] || {}),
                      updatedStatus: val,
                    },
                  }));
                }}
              >
                {handleRowLevelStatus(status)}
              </Select>
            ) : (
              <Input
                placeholder="Please enter"
                style={{ width: '200px' }}
                maxLength={64}
                showCount
                width={200}
                defaultValue={record[dataIndex]}
                onChange={(e) => {
                  setEditedValues((prev) => ({
                    [payoutId]: {
                      ...(prev[payoutId] || {}),
                      [key]: e.target.value,
                    },
                  }));
                }}
              />
            )}
          </Form.Item>
        </Form>
      </>
    ) : (
      <Tooltip title={value}>
        {
          <Typography.Text
            style={{
              width: 200,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              color: striked ? colors.colorTextTertiary : colors.colorText,
              display: 'block',
              textDecoration: striked && 'line-through',
            }}
          >
            {Type === 'Select' ? (
              <div>
                <Tag color={statusType}>{value}</Tag>
                {reattempted && isLocked && (
                  <>
                    <br /> <Tag style={{ marginTop: '6px' }}>Regenerated</Tag>
                  </>
                )}
              </div>
            ) : (
              <Text>{value}</Text>
            )}
          </Typography.Text>
        }
      </Tooltip>
    );
  };

  const columns = [
    ATable.SELECTION_COLUMN,
    ATable.EXPAND_COLUMN,
    {
      title: 'Payout ID',
      dataIndex: 'payoutId',
      key: 'payout_id',
      width: 155,
      render: (value, row) => {
        const { isLocked, striked } = row;
        return (
          <Tooltip
            title={
              <>
                {value} {isLocked && <LockOutlined />}
              </>
            }
          >
            <Typography.Text
              delete={striked}
              style={{
                display: 'block',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                cursor: 'pointer',
                color: colors.colorPrimary,
              }}
            >
              <AuditLogs
                recordId={value}
                from="payouts"
                auditData={auditData[`${value}`]}
                setPayoutAuditData={setAuditData}
              >
                {value}
                {isLocked && (
                  <LockOutlined
                    style={{
                      marginLeft: 8,
                      fontSize: '16px',
                      color: colors.colorIcon,
                    }}
                  />
                )}
              </AuditLogs>
            </Typography.Text>
          </Tooltip>
        );
      },
      sorter: true,
      sortOrder: sortField?.field === 'payoutId' ? sortField?.order : null,
    },
    {
      title: 'Contributor',
      dataIndex: 'contributorEmail',
      key: 'contributor',
      width: 260,
      render: (text, record) => {
        return (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {record.country && (
              <img
                src={getFlag(record?.country)}
                alt={record.country}
                style={{
                  width: 24,
                  height: 16,
                  marginRight: 8,
                  borderRadius: 4,
                }}
              />
            )}
            <Tooltip title={`(${record.country || ' - '}) ${text}`}>
              <Typography.Text
                delete={record.striked}
                style={{
                  width: 300,
                  display: 'block',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  color: record.striked
                    ? colors.colorTextTertiary
                    : colors.colorText,
                }}
              >
                {text}
              </Typography.Text>
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'payoutCreatedDate',
      key: 'created_date',
      width: 160,
      render: (value, record) => {
        const { striked } = record;
        return (
          <Text
            delete={record.striked}
            style={{
              color: striked ? colors.colorTextTertiary : colors.colorText,
            }}
          >
            {value}
          </Text>
        );
      },
      sorter: true,
      sortOrder:
        sortField?.field === 'payoutCreatedDate' ? sortField?.order : null,
    },
    {
      title: 'Payout Closed',
      dataIndex: 'completedDate',
      key: 'closed_date',
      width: 160,
      render: (value, record) => {
        const { striked } = record;
        return (
          <Text
            delete={record.striked}
            style={{
              color: striked ? colors.colorTextTertiary : colors.colorText,
            }}
          >
            {value}
          </Text>
        );
      },
      sorter: true,
      sortOrder: sortField?.field === 'completedDate' ? sortField?.order : null,
    },
    {
      title: 'Voided Date',
      dataIndex: 'voidedDate',
      key: 'voided_date',
      width: 160,
      render: (value, record) => {
        const { striked } = record;
        return (
          <Text
            delete={record.striked}
            style={{
              color: striked ? colors.colorTextTertiary : colors.colorText,
            }}
          >
            {value}
          </Text>
        );
      },
      sorter: true,
      sortOrder: sortField?.field === 'voidedDate' ? sortField?.order : null,
    },
    {
      title: 'Gross Amount',
      dataIndex: 'payoutAmount',
      key: 'payoutAmount',
      width: 150,
      render: (value, record) => {
        const { striked } = record;
        return (
          <Text
            delete={record.striked}
            style={{
              float: 'right',
              color: striked ? colors.colorTextTertiary : colors.colorText,
            }}
          >
            {`$ ${value?.toFixed(2)}`}
          </Text>
        );
      },
      sorter: true,
      sortOrder: sortField?.field === 'payoutAmount' ? sortField?.order : null,
    },
    {
      title: 'Fees',
      dataIndex: 'feeAmount',
      key: 'feeAmount',
      width: 100,
      render: (value, record) => {
        const { striked } = record;
        return (
          <Text
            delete={record.striked}
            style={{
              float: 'right',

              color: striked ? colors.colorTextTertiary : colors.colorText,
            }}
          >
            {value ? `$ ${value.toFixed(2)}` : null}
          </Text>
        );
      },
      sorter: true,
      sortOrder: sortField?.field === 'feeAmount' ? sortField?.order : null,
    },
    {
      title: 'Payout Rail',
      dataIndex: 'payoutRail',
      key: 'payout_rail',
      width: 150,
      render: (value, record) => {
        const { striked } = record;
        return (
          <Text
            delete={record.striked}
            style={{
              color: striked ? colors.colorTextTertiary : colors.colorText,
            }}
          >
            {value}
          </Text>
        );
      },
    },
    {
      title: 'Default Rail Changed',
      dataIndex: 'defaultRailChanged',
      key: 'defaultRailChanged',
      width: 180,
      render: (value, record) => {
        const { striked } = record;
        return (
          <>
            {value !== null && value !== undefined ? (
              <>
                <Checkbox
                  checked={defaultRailModel.key === record.payoutId || value}
                  disabled={value}
                  style={{ marginRight: 12 }}
                  onClick={() =>
                    setDefaultRailModel({ key: record.payoutId, open: true })
                  }
                />
                <Text
                  delete={record.striked}
                  style={{
                    color: striked
                      ? colors.colorTextTertiary
                      : colors.colorText,
                  }}
                >
                  {value ? 'True' : 'False'}
                </Text>
              </>
            ) : null}
          </>
        );
      },
    },
    // {
    //   title: 'Payout Period',
    //   dataIndex: 'payoutPeriod',
    //   key: 'payout_period',
    //   width: 160,
    //   render: (value, record) => {
    //     const { striked } = record;
    //     return (
    //       <Text
    //         delete={record.striked}
    //         style={{
    //           color: striked ? colors.colorTextTertiary : colors.colorText,
    //         }}
    //       >
    //         {value}
    //       </Text>
    //     );
    //   },
    // },
    {
      title: 'Payout Status',
      dataIndex: 'status',
      key: 'payout_status',
      width: 240,
      render: (value, record) =>
        handleEditColumn(value, record, 'Select', 'updatedStatus', 'status'),
    },
    {
      title: 'Attempt',
      dataIndex: 'attempt',
      key: 'attempt',
      width: 210,
      render: (value, record) => {
        const { striked, reattemptStatus, reattemptStatusType } = record;
        return (
          <Text
            style={{
              color: striked ? colors.colorTextTertiary : colors.colorText,
              display: 'flex',
              textDecoration: striked && 'line-through',
            }}
          >
            <>
              {value && <Tag>{value}</Tag>}
              {reattemptStatus && (
                <Tag color={reattemptStatusType}>{reattemptStatus}</Tag>
              )}
            </>
          </Text>
        );
      },
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'Notes',
      width: 240,
      render: (value, record) =>
        handleEditColumn(value, record, 'input', 'updatedNotes', 'notes'),
    },
    {
      title: 'Failure Reason',
      dataIndex: 'failureNotes',
      key: 'failure_Reson',
      width: 240,
      render: (value, record) =>
        handleEditColumn(
          value,
          record,
          'input',
          'updatedFailureNotes',
          'failureNotes',
        ),
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (record) => {
        const { payoutId, isLocked } = record;
        return editableRows.includes(payoutId) ? (
          <div style={{ display: 'flex' }}>
            <CustomButton
              htmlType="reset"
              type="primary"
              variant="text"
              icon={<CloseOutlined />}
              onClick={() => {
                setEditableRows([]), setEditedValues({});
              }}
              size="middle"
              shape="circle"
            />
            <CustomButton
              htmlType="submit"
              type="primary"
              variant="text"
              icon={<CheckOutlined />}
              onClick={() => {
                handlePayoutsStatusUpdate(
                  'single',
                  payoutId,
                  editedValues[payoutId],
                );
              }}
              size="middle"
              shape="circle"
            />
          </div>
        ) : (
          <>
            <EditOutlined
              style={
                isLocked
                  ? {
                      pointerEvents: 'none',
                      opacity: 0.5,
                      cursor: 'not-allowed',
                      marginLeft: 8,
                    }
                  : {
                      cursor: 'pointer',
                      color: colors.colorPrimary,
                      marginLeft: 8,
                    }
              }
              onClick={() => setEditableRows([record.payoutId])}
            />
            {canCompleteFailedPayout(record) && (
              <Tooltip
                title={
                  isCompleteFailedPayoutLocked
                    ? 'Completing a payout is unavailable while another batch job is running'
                    : 'Complete payout'
                }
              >
                <CheckCircleOutlined
                  name="tb-complete-failed-payout-icon"
                  style={
                    isCompleteFailedPayoutLocked
                      ? {
                          opacity: 0.5,
                          cursor: 'not-allowed',
                          color: colors.colorTextTertiary,
                          marginLeft: 8,
                        }
                      : {
                          cursor: 'pointer',
                          color: colors.colorPrimary,
                          marginLeft: 8,
                        }
                  }
                  onClick={() =>
                    !isCompleteFailedPayoutLocked &&
                    setCompleteModal({ open: true, payoutId })
                  }
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    const { field, order } = sorter;
    handleSort({ field, order });
  };

  const handleDefaultRail = () => {
    setDeafultRailLoader(true);
    const payload = { payoutId: defaultRailModel.key, defaultRail: true };
    defaultRail(payload)
      .then(() => {
        setDefaultRailModel({ key: '', open: false });
        handlePayouts();
        message.success('Default rail successfully updated');
      })
      .catch(() => {
        message.error('Unable to update default rail');
        setDefaultRailModel({ key: '', open: false });
      })
      .finally(() => {
        setDeafultRailLoader(false);
      });
  };

  return (
    <div style={{ margin: '24px 0' }}>
      <ATable
        title={title}
        loading={loading}
        onFilter
        style={{ height: '100%' }}
        columns={columns}
        dataSource={data}
        pagination={false}
        onChange={handleTableChange}
        rowSelection={{
          preserveSelectedRowKeys: true,
          fixed: true,
          // getCheckboxProps: (record) => ({
          //   disabled:
          //     record.isLocked &&
          //     (record.status !== 'Payout Failure' || record.reattempted),
          // }),
          onChange: (selectedRowKeys, selectedRecords, info) => {
            if (selectedRowKeys.length >= FileSize.SELECT_SIZE) {
              message.warning('You can select up to 30,000 rows only.');
            }
            handleRowSelection(selectedRowKeys, selectedRecords, info);
          },
          getCheckboxProps: (record) => {
            const isSelected = selectedRowKeys.includes(record.key);
            const disable =
              !isSelected && selectedRowKeys.length >= FileSize.SELECT_SIZE;

            return {
              disabled: disable,
            };
          },
        }}
        expandable={{
          showExpandColumn: false,
          expandedRowRender: (record) =>
            ExpandedRow(
              record,
              expandedRecords,
              loadingTransactionData,
              setExpandedRecords,
              TransactionsLoader,
              SetTransactionsLoader,
              oldPayoutExpandedRowKeys,
              setOldPayoutExpandedRowKeys,
            ),
          expandedRowKeys: payoutExpandedRowKeys,
          onExpand: async (expanded, record) => {
            setPayoutExpandedRowKeys(expanded ? [record.key] : []);
            if (expanded) {
              if (!expandedRecords[`${record.payoutId}`]) {
                setLoadingTransactionData(true);
                getPayoutsAndInvoices(record.payoutId)
                  .then((res) => {
                    const { invoices, payouts } = res;
                    let invoice_map = invoices.map((row) => {
                      return {
                        ...row,
                        key: row.invoiceId,
                        parent_key: `${record.payoutId}`,
                      };
                    });

                    let payout_map = payouts.map((row) => {
                      return {
                        ...row,
                        key: row.payoutId,
                        parent_key: `${record.payoutId}`,
                      };
                    });
                    setExpandedRecords({
                      ...expandedRecords,
                      [`${record.payoutId}`]: {
                        invoices: invoice_map,
                        payouts: payout_map,
                        transactions: [],
                      },
                    });
                  })
                  .catch(() => {
                    message.error('Failed to fetch invoice data');
                  })
                  .finally(() => {
                    setLoadingTransactionData(false);
                  });
              }
            }
          },
        }}
        bordered
        scroll={{
          y: 'calc(100vh - 250px)',
        }}
      />

      <Modal
        title={
          <div style={{ display: 'flex', gap: '8px' }}>
            <ExclamationCircleOutlined
              style={{ color: colors.colorWarning, width: 21, height: 21 }}
            />{' '}
            Confirm Update to Default Rail
            <br /> Changed
          </div>
        }
        closable={false}
        open={defaultRailModel.open}
        width={'45%'}
        footer={[
          <Button
            key="back"
            onClick={() => setDefaultRailModel({ key: '', open: false })}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={defaultRailLoader}
            onClick={() => {
              handleDefaultRail();
            }}
          >
            Confirm
          </Button>,
        ]}
        centered
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            margin: '0px 28px',
          }}
        >
          <Text>
            You’re about to mark this record as
            <br />
            <b>Default Rail Changed = True.</b>
          </Text>
          <Text>
            This will update the value from <b>False to True</b> and cannot be
            undone.
          </Text>
          <Text>Do you want to proceed?</Text>
        </div>
      </Modal>

      <CompleteFailedPayoutModal
        open={completeModal.open}
        setOpen={(open) => setCompleteModal({ ...completeModal, open })}
        payoutId={completeModal.payoutId}
        loading={completeLoading}
        onOk={handleCompleteSubmit}
      />
    </div>
  );
}
